/** Comparable local measurements only; raw throughput remains the source of truth. */
export type PerformanceMeasurement = {
  readonly workloadFingerprint: string;
  readonly mode: 'local' | 'cluster';
  readonly complete: boolean;
  readonly warmPagesPerMinute: number;
  /**
   * A second workload this run's own record proves it also did, request for request
   * (`processorPacedSmallWaveWorkload` in benchmark-plan.ts is the only source). Never set from
   * matching throughput or hardware.
   */
  readonly sameWorkAs?: string;
};

export type PerformanceCalibration = {
  readonly version: string;
  readonly workloadFingerprint: string;
  readonly lowerPagesPerMinute: number;
  readonly upperPagesPerMinute: number;
};

/** Exact historical aliases, proved by rehashing identical sample bytes/settings
 * with only appVersion changed (6.1.158/6.1.164). Never infer compatibility from
 * matching throughput or hardware. Original saved receipts remain untouched. */
export function canonicalPerformanceWorkload(fingerprint: string): string {
  if (
    fingerprint === 'd3c960aa9b4e420116e144b9f4a6001140c3be3f2a3e4675ea8abe58cdb34d85' ||
    fingerprint === '1b2e58d8e42c4b246c9d07b12ea054e3f00cbe426b97a98699eb89da5ddd68a1' ||
    fingerprint === 'b1afd69ff18edb8f44970a48984e5e17e5e10cdcbfb87c07dac539a34c2f6c2f'
  )
    return '32931d01b406cecbcaaea689632594752c5357c86c7fea8ada18246d8873d164';
  return fingerprint;
}

/**
 * The timed test's two workload identities over the shipped Accounts Payable samples, as the
 * harness hashes them (`timedPerformanceSettings` in main/benchmark-harness.ts). v7 grouped the
 * sample files by the folder import's waves for the hardware (2 then 40 on a graphics card, 2
 * then 8 on the processor). The small-wave test (v8) groups them 2 then 8 on every computer.
 * tests/benchmark/speed-test-waves.test.mjs rehashes the samples to both, so a change to the
 * samples, schemas or profile that moves either identity fails there, not silently here.
 */
export const timedV7PerformanceWorkload =
  '32931d01b406cecbcaaea689632594752c5357c86c7fea8ada18246d8873d164';
export const smallWavePerformanceWorkload =
  'e0aae15a30bafd028c8f7ccbd81050fb09e46e4349122d675cb797a4e83ba12e';

/**
 * The one workload two measurements share, or null: the same identity, or one of them proved
 * to have done the other's exact work (`sameWorkAs`).
 */
export function sharedPerformanceWorkload(
  first: Pick<PerformanceMeasurement, 'workloadFingerprint' | 'sameWorkAs'>,
  second: Pick<PerformanceMeasurement, 'workloadFingerprint' | 'sameWorkAs'>,
): string | null {
  const one = canonicalPerformanceWorkload(first.workloadFingerprint),
    other = canonicalPerformanceWorkload(second.workloadFingerprint);
  if (one === other) return first.workloadFingerprint;
  if (first.sameWorkAs !== undefined && canonicalPerformanceWorkload(first.sameWorkAs) === other)
    return second.workloadFingerprint;
  if (second.sameWorkAs !== undefined && canonicalPerformanceWorkload(second.sameWorkAs) === one)
    return first.workloadFingerprint;
  return null;
}

function usable(value: PerformanceMeasurement): boolean {
  return (
    /^[a-f0-9]{64}$/.test(value.workloadFingerprint) &&
    value.mode === 'local' &&
    value.complete === true &&
    Number.isFinite(value.warmPagesPerMinute) &&
    value.warmPagesPerMinute >= 0
  );
}

/** Freeze measured laptop/desktop anchors at 5/80. No hardware identity lookup. */
export function calibratePerformanceScore(
  version: string,
  lower: PerformanceMeasurement,
  upper: PerformanceMeasurement,
): PerformanceCalibration | null {
  const workloadFingerprint = sharedPerformanceWorkload(lower, upper);
  if (
    !version.trim() ||
    !usable(lower) ||
    !usable(upper) ||
    workloadFingerprint === null ||
    upper.warmPagesPerMinute <= lower.warmPagesPerMinute
  )
    return null;
  return {
    version,
    workloadFingerprint,
    lowerPagesPerMinute: lower.warmPagesPerMinute,
    upperPagesPerMinute: upper.warmPagesPerMinute,
  };
}

/** Power curve preserves relative speed ordering and headroom beyond the desktop. */
export function measuredPerformanceScore(
  value: PerformanceMeasurement,
  calibration: PerformanceCalibration,
): number | null {
  const low = calibration.lowerPagesPerMinute,
    high = calibration.upperPagesPerMinute;
  if (
    !usable(value) ||
    !calibration.version.trim() ||
    sharedPerformanceWorkload(value, calibration) === null ||
    !Number.isFinite(low) ||
    !Number.isFinite(high) ||
    low < 0 ||
    high <= low
  )
    return null;
  // A deadline-limited CPU sample can validly finish zero pages. There is no
  // positive throughput to take a logarithm of. Use the explicit 5/80 affine
  // reference scale for that case, preserving the raw zero beside the score.
  // All zero-completion machines tie; do not infer their sub-page speeds.
  if (low === 0)
    return Math.round(Math.min(100, Math.max(1, 5 + (75 * value.warmPagesPerMinute) / high)));
  if (value.warmPagesPerMinute === 0) return 1;
  // The reference desktop is strong, not close to the ceiling. Reserve 100
  // for four times its measured throughput on this same workload. A separate
  // upper segment keeps the 5/80 anchors exact without an easily saturated cap.
  if (value.warmPagesPerMinute >= high)
    return Math.min(
      100,
      80 + Math.floor((20 * Math.log(value.warmPagesPerMinute / high)) / Math.log(4)),
    );
  const exponent = Math.log(80 / 5) / (Math.log(high) - Math.log(low));
  const logScore = Math.log(5) + exponent * (Math.log(value.warmPagesPerMinute) - Math.log(low));
  // Clamp in log space, including for extremely slow/fast valid measurements.
  return Math.round(Math.exp(Math.min(Math.log(100), Math.max(0, logScore))));
}
